// Only identifying fields belong in a dialog heading; never use arbitrary record values.
const identifyingFields = {
  domains: ['fullDomain', 'domain'],
  contacts: ['fullHandle', 'handle', 'companyName'],
  'customer-users': ['fullName', 'userInfo.fullName', 'userInfo.email'],
  'domain-ns-groups': ['alias', 'handle'],
  dmarc: ['domain'],
  'ssl-certificates': ['commonName'],
  webhosting: ['username', 'primaryDomain'],
  'customer-phone-numbers': ['number', 'label'],
  'customer-sip-accounts': ['name', 'username'],
  invoices: ['invoiceNumber', 'invoiceReference', 'customerInfo.name'],
  'customer-offers': ['offerNumber', 'offerReference', 'description', 'customerInfo.name'],
  'sales-orders': ['orderReference', 'description', 'customerInfo.name'],
  'purchase-orders': ['number', 'orderReference'],
  assets: ['serialNumber', 'productInfo.name'],
  stock: ['productInfo.name'],
  subscriptions: ['invoiceDescriptionFull', 'productInfo.invoiceDescription', 'productInfo.name'],
  sepa: ['batchId'],
  'direct-debit': ['batchId'],
  'email-queue': ['subject'],
  settings: ['name', 'key'],
}

const text = (value) => (typeof value === 'string' || typeof value === 'number' ? String(value).replace(/\s+/g, ' ').trim() : '')

const firstLine = (value) => text(typeof value === 'string' ? value.split(/\r\n?|\n|\u2028|\u2029/, 1)[0] : value)

const personName = (record) => [record?.firstName, record?.lastName].map(text).filter(Boolean).join(' ')

// Use editable source fields first, because API-computed labels on a clone can be stale.
const composedNames = {
  domains: (record) => {
    const domain = text(record.domain)
    const tld = text(record.tld).replace(/^\./, '')
    return domain && tld ? `${domain}.${tld}` : ''
  },
  'ssl-certificates': (record) => (Array.isArray(record.commonName) ? record.commonName.map(text).filter(Boolean).join(', ') : ''),
  contacts: (record) => {
    const name = personName(record)
    const company = text(record.companyName)
    const label = [name, company && (name ? `(${company})` : company)].filter(Boolean).join(' ')
    return label ? [text(record.handle), label].filter(Boolean).join(' - ') : ''
  },
  'customer-contacts': personName,
  users: personName,
  'customer-users': (record) => personName(record) || personName(record.userInfo),
  'sharingcenter-contacts': personName,
  'domain-ns-groups': (record) => [text(record.handle), text(record.alias)].filter(Boolean).join(' - '),
  webhosting: (record) => [text(record.username), text(record.primaryDomain)].filter(Boolean).join(' - '),
  subscriptions: (record) => {
    const description = firstLine(record.invoiceDescription)
    const product = record.productInfo?.invoiceDescription || record.productInfo?.name
    if (!description.includes('%productDescription%')) return description
    return typeof product === 'string' ? firstLine(description.replaceAll('%productDescription%', product)) : ''
  },
}

export const getDialogRecordName = (service, record) => {
  if (!record) return ''
  const composedName = composedNames[service]?.(record)
  if (composedName) return composedName
  for (const path of identifyingFields[service] || ['name', 'fullName']) {
    const rawValue = path.split('.').reduce((item, key) => item?.[key], record)
    const value = service === 'subscriptions' ? firstLine(rawValue) : text(rawValue)
    if (value) return value
  }
  return personName(record) || text(record.email) || text(record.emailAddress) || text(record._id)
}
